import { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Plus, Bell } from "lucide-react";
import NotificationRuleRow from "@/components/notifications/NotificationRuleRow";
import DeadlineReminderField from "@/components/notifications/DeadlineReminderField";
import { DASHBOARD_BADGE_OPTIONS } from "@/lib/sidebarBadges";

function genId() {
  return `rule_${Math.random().toString(36).slice(2, 10)}_${Date.now().toString(36)}`;
}

export default function NotificationPreferencesDialog({ open, onOpenChange, userEmail, userRole }) {
  const [catalog, setCatalog] = useState([]);
  const [rules, setRules] = useState([]);
  const [deadlineDays, setDeadlineDays] = useState(1);
  const [pushNotificationsEnabled, setPushNotificationsEnabled] = useState(true);
  const [dashboardBadges, setDashboardBadges] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [catRes, prefsRes] = await Promise.all([
          api.get("/notifications/catalog"),
          api.get("/me/notification-preferences"),
        ]);
        if (cancelled) return;
        setCatalog(catRes.data?.events || []);
        setRules(prefsRes.data?.preferences || []);
        setDeadlineDays(prefsRes.data?.deadline_notification_days ?? 1);
        setPushNotificationsEnabled(prefsRes.data?.push_notifications_enabled !== false);
        setDashboardBadges(prefsRes.data?.dashboard_badges || {});
      } catch (err) {
        if (!cancelled) toast.error(formatApiError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const addRule = useCallback(() => {
    setRules((prev) => [
      ...prev,
      {
        id: genId(),
        event_type: catalog[0]?.key || "custom",
        enabled: true,
        email_enabled: true,
        push_enabled: true,
        label: "",
        email: userEmail || "",
        keyword: "",
      },
    ]);
  }, [catalog, userEmail]);

  const updateRule = useCallback((id, patch) => {
    setRules((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }, []);

  const removeRule = useCallback((id) => {
    setRules((prev) => prev.filter((r) => r.id !== id));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put("/me/notification-preferences", {
        preferences: rules,
        deadline_notification_days: Number(deadlineDays) || 0,
        push_notifications_enabled: !!pushNotificationsEnabled,
        dashboard_badges: dashboardBadges,
      });
      setRules(res.data?.preferences || []);
      setDeadlineDays(res.data?.deadline_notification_days ?? deadlineDays);
      setPushNotificationsEnabled(res.data?.push_notifications_enabled !== false);
      setDashboardBadges(res.data?.dashboard_badges || {});
      window.dispatchEvent(new CustomEvent("whitenton:sidebar-badges-changed", {
        detail: res.data?.dashboard_badges || {},
      }));
      toast.success("Notification preferences saved");
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const optInEvents = catalog.filter((ev) => ev.default_enabled === false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-white dark:bg-[#1e1e1e] max-w-3xl max-h-[85vh] overflow-y-auto"
        data-testid="notification-preferences-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Bell className="h-5 w-5" /> Notification Preferences
          </DialogTitle>
          <DialogDescription>
            Control sidebar counts, email alerts, and mobile push independently.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-sm text-[#647069]" data-testid="notif-loading">Loading…</div>
        ) : (
          <div className="space-y-4">
            <DeadlineReminderField days={deadlineDays} onChange={setDeadlineDays} />

            <div className="flex items-center justify-between gap-4 rounded-md border border-[#284A36]/20 bg-[#F7F8F6] dark:bg-white/5 p-3">
              <div>
                <p className="text-sm font-semibold text-[#16201A] dark:text-white">Mobile push notifications</p>
                <p className="text-xs text-[#647069] dark:text-white/60">Controls phone notifications only. Email rules below still apply.</p>
              </div>
              <Switch
                checked={pushNotificationsEnabled}
                onCheckedChange={setPushNotificationsEnabled}
                data-testid="notif-push-global-enabled"
              />
            </div>

            <div className="rounded-md border border-[#E5E7E2] dark:border-white/10 p-3 space-y-3" data-testid="dashboard-badge-preferences">
              <div>
                <p className="text-sm font-semibold text-[#16201A] dark:text-white">Dashboard and browser alert counts</p>
                <p className="text-xs text-[#647069] dark:text-white/60">Choose which counts appear in the sidebar and contribute to the alert at the top of your browser. This does not change email or mobile push delivery.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {DASHBOARD_BADGE_OPTIONS.filter((option) => option.roles.includes(userRole)).map((option) => (
                  <div key={option.key} className="flex items-center justify-between gap-3 rounded border border-[#E5E7E2] dark:border-white/10 px-3 py-2">
                    <span className="text-sm">{option.label}</span>
                    <Switch
                      checked={dashboardBadges[option.key] !== false}
                      onCheckedChange={(checked) => setDashboardBadges((prev) => ({ ...prev, [option.key]: checked }))}
                      data-testid={`dashboard-badge-toggle-${option.key}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {optInEvents.length > 0 && (
              <div className="p-3 rounded-md border border-[#284A36]/20 bg-[#284A36]/5 text-[12px] text-[#16201A]" data-testid="notif-opt-in-banner">
                <strong>Opt-in events:</strong> The following are <em>off by default</em>—add a rule below and toggle it on to receive them:
                <ul className="list-disc list-inside mt-1 space-y-0.5">
                  {optInEvents.map((ev) => <li key={ev.key}>{ev.label}</li>)}
                </ul>
              </div>
            )}

            {rules.length === 0 && (
              <div
                className="py-8 text-center text-sm text-[#647069] dark:text-white/60 border border-dashed rounded-md"
                data-testid="notif-empty-state"
              >
                No rules yet. Add one below to start receiving notification alerts.
              </div>
            )}

            {rules.map((rule, idx) => (
              <NotificationRuleRow
                key={rule.id}
                rule={rule}
                index={idx}
                catalog={catalog}
                userEmail={userEmail}
                onChange={updateRule}
                onRemove={removeRule}
              />
            ))}

            <Separator />
            <Button type="button" variant="outline" onClick={addRule} className="w-full" data-testid="notif-add-rule">
              <Plus className="h-4 w-4 mr-2" /> Add Rule
            </Button>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="notif-cancel">
            Cancel
          </Button>
          <Button
            type="button"
            className="bg-[#284A36] hover:bg-[#1E3A28] text-white"
            onClick={handleSave}
            disabled={saving || loading}
            data-testid="notif-save"
          >
            {saving ? "Saving…" : "Save Preferences"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
